import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../shared/auth/auth.service';

interface MarketplaceEntryResponse {
  entryPath: string;
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class MarketplaceEntryService {
  // Only the configured Marketplace receives the existing HUNGERY credential.
  // Using HttpBackend avoids unrelated ERP refresh/login interceptors on this
  // cross-origin, one-use exchange. No token is stored or placed in a URL.
  private readonly http: HttpClient;
  private readonly origin = (environment as { marketplaceRoot?: string }).marketplaceRoot || '';

  constructor(backend: HttpBackend, private authService: AuthService) {
    this.http = new HttpClient(backend);
  }

  get isConfigured(): boolean {
    return this.origin === 'https://storestaging.evolution-erp.systems';
  }

  createEntry(): Observable<string> {
    if (!this.isConfigured) {
      return throwError(() => new HttpErrorResponse({ status: 503 }));
    }
    const token = this.authService.getToken()?.trim();
    if (!token || token.length > 32768 || /\s/.test(token)) {
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }
    return this.http.post<MarketplaceEntryResponse>(
      this.origin + '/api/v1/entry-tokens/hungery', null,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + token,
          'X-Marketplace-Request': '1',
        }),
        withCredentials: false,
      }
    ).pipe(
      timeout(15000),
      map(response => {
        // Accept only this product's short-lived entry code. An unexpected
        // redirect, additional query parameter or an expired code is rejected.
        const expiresAt = Date.parse(response?.expiresAt || '');
        if (!response || !/^\/start\/hungery\?code=[a-fA-F0-9]{64}$/.test(response.entryPath) ||
            !Number.isFinite(expiresAt) || expiresAt <= Date.now() || expiresAt > Date.now() + 5 * 60 * 1000) {
          throw new HttpErrorResponse({ status: 503 });
        }
        return this.origin + response.entryPath;
      })
    );
  }
}
